import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

/** A single destination in the {@link TabBarComponent}. */
export interface TabBarItemI {
  /** Stable identifier used to flag the active tab and emitted on selection. */
  id: string;
  /** Visible label rendered under the icon. */
  label: string;
  /** Boxicons class (e.g. `bx-grid-alt`). */
  icon: string;
  /** Optional numeric badge (e.g. unread count). Falsy values render no badge. */
  badge?: number;
}

/**
 * TabBar (#1142) — a generic bottom tab bar for responsive shells (the assessment
 * wizard #1156 + future customer/operator shells).
 *
 * Deliberately router-agnostic: unlike the admin-shell `MobileTabBarComponent`,
 * this primitive carries no `RouterLink` coupling. It takes the tabs + the active
 * tab id as inputs and emits `tabSelected` so the host decides how to navigate
 * (router, signal store, internal wizard step, etc.). Presentational only.
 *
 * Accessibility:
 *  - `role="tablist"` with one `role="tab"` button per item.
 *  - `aria-selected` + `aria-current="page"` on the active tab.
 *  - Real `<button>`s so tabs are keyboard-focusable and activatable.
 *
 * Token-driven styling only (deep-ocean surface, amber active indicator).
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-tab-bar',
  standalone: true,
  templateUrl: './tab-bar.component.html',
  styleUrls: ['./tab-bar.component.scss'],
})
export class TabBarComponent {
  /** Tabs to render along the bottom of the viewport. */
  readonly tabs = input<TabBarItemI[]>([]);
  /** Id of the currently active tab. */
  readonly activeTabId = input<string>('');
  /** Optional accessible label for the tablist. */
  readonly ariaLabel = input<string>('Primary');

  /** Emits the id of the tab the user activated. */
  readonly tabSelected = output<string>();

  protected readonly hasActive = computed(() => this.tabs().some(t => t.id === this.activeTabId()));

  protected isActive(tab: TabBarItemI): boolean {
    return tab.id === this.activeTabId();
  }

  protected handleSelect(tab: TabBarItemI): void {
    this.tabSelected.emit(tab.id);
  }
}
